/**
 * Time label utilities for displaying relative dates
 * Prevents showing placeholder values like "999 days"
 */

export function daysBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export function formatRelativeDays(days: number): string {
  if (days === 0) return "today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

/**
 * Get contact label for "Why now" / "Never contacted" sections
 * Returns "Never contacted" if never contacted, otherwise "X days ago"
 */
export function getContactLabel(opts: {
  neverContacted: boolean;
  lastInteractionAt?: string | Date | null;
}): string {
  if (opts.neverContacted) return "Never contacted";
  
  const d = opts.lastInteractionAt ? new Date(opts.lastInteractionAt) : null;
  if (!d || isNaN(d.getTime())) return "—";
  
  const daysAgo = Math.max(0, daysBetween(d, new Date()));
  return `${formatRelativeDays(daysAgo)} ago`;
}

/**
 * Get follow-up label for overdue or upcoming actions
 * Returns null if no follow-up date, otherwise "Due in X days" or "Overdue by X days"
 */
export function getFollowUpLabel(opts: {
  nextFollowUpAt?: string | Date | null;
  nextActionAt?: string | Date | null;
}): string | null {
  // Support both nextFollowUpAt and nextActionAt
  const due = opts.nextFollowUpAt || opts.nextActionAt;
  const dueDate = due ? new Date(due) : null;
  
  if (!dueDate || isNaN(dueDate.getTime())) return null;

  const diff = daysBetween(new Date(), dueDate); // positive = future, negative = overdue
  if (diff === 0) return "Due today";
  if (diff > 0) return `Due in ${formatRelativeDays(diff)}`;
  return `Overdue by ${formatRelativeDays(Math.abs(diff))}`;
}

/**
 * Sanitize days value from backend to prevent showing 999 or unrealistic values
 * If value >= 365 and neverContacted, return null to show "Never contacted"
 * If value >= 365 and not neverContacted but no date, return null to show "—"
 */
export function sanitizeDaysValue(
  value: number | null | undefined,
  neverContacted: boolean,
  hasDate: boolean
): number | null {
  if (value == null) return null;
  if (value >= 365) {
    if (neverContacted) return null; // Will show "Never contacted"
    if (!hasDate) return null; // Will show "—"
  }
  return value;
}

